import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Sparkles,
  Plus,
  Trash2,
  ExternalLink,
  Image as ImageIcon,
  Eye,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Gauge,
  RefreshCw,
  History,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import {
  BLOG_BLOCK_KIND_LABELS,
  BLOG_LEVEL_LABELS,
  BLOG_TARGET_ROLE_OPTIONS,
  DEFAULT_BLOG_EVAL_THRESHOLD,
  MAX_BLOG_EVAL_ITERATIONS,
  blogArticleIssues,
  blogStatusLabel,
  blogGenProgress,
  blocksBelowThreshold,
  isArticlePerfect,
  scoresAboveThreshold,
  formatAxisScores,
  normalizeBlogArticle,
  type BlogArticle,
  type BlogBlock,
  type BlogLevel,
  type BlogPenseur,
  type BlogResource,
  type BlogBlockEvaluation,
  type BlogBlockVersion,
  type BlogAxisScores,
  type BlogEvaluation,
} from '@jeraisonne/db';
import {
  useAdminBlogArticle,
  useAdminBlogMetrics,
  useGenerateBlogCover,
  useGenerateBlogPoll,
  useRegenerateBlogArticle,
  useUpdateBlogArticle,
  useLatestBlogEvaluation,
  useBlogEvaluations,
  useBlockVersions,
  useRefineBlogBlock,
  useSwitchBlockVersion,
  useDeleteBlockVersion,
  useSubmitBlogForReview,
  useWithdrawBlogReview,
  useModerateBlogArticle,
  blogEvaluationQueryKey,
  blogEvaluationsQueryKey,
  blockVersionsQueryKey,
} from '@/lib/admin/use-blog';
import {
  evaluateBlogGlobal,
  evaluateBlogBlock,
  persistBlogEvaluation,
  type BlogPatch,
} from '@/lib/admin/blog-client';
import { useAuth, isAdmin } from '@/lib/auth';
import { SocialPublishSection } from '@/features/blog-social/social-publish-section';

const inputCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2';
const areaCls = `${inputCls} resize-y`;
const labelCls = 'font-display text-ink-700 text-[12px] font-semibold';

const SITE_URL =
  (import.meta.env.VITE_MARKETING_URL as string | undefined)?.replace(/\/$/, '') ?? '';

const SOURCE_LABELS: Record<BlogBlockVersion['source'], string> = {
  generation: 'génération',
  refine_ai: 'régénéré (IA)',
  manual: 'manuel',
};

/** Badge de notes inline (font-mono) ; texte ambre si une note < seuil. */
function ScoreLine({
  scores,
  threshold,
}: {
  scores: BlogAxisScores | null | undefined;
  threshold: number;
}) {
  const below = !scoresAboveThreshold(scores, threshold);
  return (
    <p className={`font-mono text-[11px] ${below ? 'text-accent-600' : 'text-ink-500'}`}>
      {formatAxisScores(scores)}
    </p>
  );
}

/** Liste compacte des axes de progression (pistes d'amélioration). */
function AxesList({ items }: { items: string[] | null | undefined }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="text-ink-500 mt-1 list-inside list-disc space-y-0.5 font-sans text-[12px]">
      {items.map((it, k) => (
        <li key={k}>{it}</li>
      ))}
    </ul>
  );
}

function Section({
  title,
  subtitle,
  children,
  collapsible,
  collapsed,
  onToggle,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  collapsible?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
}) {
  if (collapsible) {
    return (
      <section className="border-ink-200 overflow-hidden rounded-2xl border bg-white">
        <button
          type="button"
          onClick={onToggle}
          className="hover:bg-ink-50/60 flex min-h-11 w-full items-center gap-2 px-4 py-3.5 text-left transition-colors sm:px-5"
        >
          {collapsed ? (
            <ChevronRight className="text-ink-400 h-4 w-4 shrink-0" />
          ) : (
            <ChevronDown className="text-ink-400 h-4 w-4 shrink-0" />
          )}
          <h2 className="font-display text-ink-900 shrink-0 text-[15px] font-extrabold">{title}</h2>
          {subtitle && (
            <span className="text-ink-400 min-w-0 flex-1 truncate font-sans text-[12px]">
              {subtitle}
            </span>
          )}
        </button>
        {!collapsed && <div className="px-4 pb-4 sm:px-5 sm:pb-5">{children}</div>}
      </section>
    );
  }
  return (
    <section className="border-ink-200 rounded-2xl border bg-white p-4 sm:p-5">
      <h2 className="font-display text-ink-900 mb-3 text-[15px] font-extrabold">{title}</h2>
      {children}
    </section>
  );
}

function editablePatch(d: BlogArticle): BlogPatch {
  return {
    title: d.title,
    subtitle: d.subtitle,
    slug: d.slug,
    illustration_url: d.illustration_url,
    illustration_prompt: d.illustration_prompt,
    blocks: d.blocks,
    penseurs: d.penseurs,
    pour_aller_plus_loin: d.pour_aller_plus_loin,
    social: d.social,
    seo: d.seo,
    conclusion_md: d.conclusion_md,
    sources_utilisees: d.sources_utilisees,
    reading_time: d.reading_time,
    published_at: d.published_at,
    target_roles: d.target_roles,
  };
}

export function TutorBlogDetailPage() {
  const { articleId } = useParams({ strict: false }) as { articleId: string };
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const { data: article, isLoading } = useAdminBlogArticle(articleId);
  const { data: metrics = [] } = useAdminBlogMetrics(articleId);
  const update = useUpdateBlogArticle(articleId);
  const cover = useGenerateBlogCover(articleId);
  const poll = useGenerateBlogPoll(articleId);
  const regenerate = useRegenerateBlogArticle(articleId);
  const qc = useQueryClient();
  const refine = useRefineBlogBlock(articleId);
  const switchVer = useSwitchBlockVersion(articleId);
  const deleteVer = useDeleteBlockVersion(articleId);
  const submitReview = useSubmitBlogForReview(articleId);
  const withdrawReview = useWithdrawBlogReview(articleId);
  const moderate = useModerateBlogArticle(articleId);
  const { data: latestEval } = useLatestBlogEvaluation(articleId);
  const { data: evalHistory = [] } = useBlogEvaluations(articleId);
  const { data: versions = [] } = useBlockVersions(articleId);

  const [draft, setDraft] = useState<BlogArticle | null>(null);
  // Raffinage itératif : seuil de note, remarques manuelles par bloc, états busy.
  const [threshold, setThreshold] = useState(DEFAULT_BLOG_EVAL_THRESHOLD);
  const [manualRemarks, setManualRemarks] = useState<Record<number, string>>({});
  const [refineBusy, setRefineBusy] = useState<number | null>(null);
  const [globalRefining, setGlobalRefining] = useState(false);
  const [refineError, setRefineError] = useState<string | null>(null);
  const [evalError, setEvalError] = useState<string | null>(null);
  const [saveWarning, setSaveWarning] = useState<string | null>(null);
  // Évaluation morcelée en cours : résultat partiel affiché au fur et à mesure.
  const [liveEval, setLiveEval] = useState<BlogEvaluation | null>(null);
  const [evalRunning, setEvalRunning] = useState(false);
  const [evalProgress, setEvalProgress] = useState<{
    done: number;
    total: number;
    label: string;
  } | null>(null);
  // Sections dépliées (par id) — par défaut tout est replié pour alléger l'édition
  // d'un article long. Un id absent du set = cadre replié.
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  // Sondage en cours de (re)génération : index du bloc, + erreur éventuelle.
  const [pollBusy, setPollBusy] = useState<number | null>(null);
  const [pollError, setPollError] = useState<{ index: number; message: string } | null>(null);
  const genStatus = article?.generation_status;

  // Initialise le brouillon local quand l'article est prêt (pas pendant la
  // génération, sinon on figerait un contenu vide).
  useEffect(() => {
    if (article && genStatus !== 'generating' && !draft) setDraft(normalizeBlogArticle(article));
  }, [article, genStatus, draft]);

  const issues = useMemo(() => (draft ? blogArticleIssues(draft) : []), [draft]);

  if (isLoading || !article) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  // Génération asynchrone en cours → écran d'attente (le hook poll toutes les 3 s).
  if (genStatus === 'generating') {
    const prog = blogGenProgress(article.generation_step);
    return (
      <div className="mx-auto max-w-2xl p-4 sm:p-6">
        <Link
          to="/tutor/blog"
          className="text-ink-500 hover:text-ink-900 mb-6 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Blog réflexif
        </Link>
        <div className="border-ink-200 flex flex-col items-center gap-4 rounded-2xl border bg-white px-6 py-16 text-center">
          <div className="border-primary-500 h-10 w-10 animate-spin rounded-full border-2 border-t-transparent" />
          <div>
            <p className="font-display text-ink-900 text-[16px] font-extrabold">
              Génération en cours…
            </p>
            <p className="text-ink-500 mt-1 font-sans text-[13px]">
              L&apos;IA construit le parcours étape par étape. Compte ~2 à 3 minutes. Cette page se
              met à jour toute seule.
            </p>
          </div>

          {/* Étape courante + barre de progression */}
          <div className="w-full max-w-xs">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="font-display text-ink-700 text-[13px] font-semibold">
                {prog.label}
              </span>
              <span className="text-ink-400 font-sans text-[11px]">
                {prog.index}/{prog.total}
              </span>
            </div>
            <div className="bg-ink-100 h-2 w-full overflow-hidden rounded-full">
              <div
                className="bg-primary-500 h-full rounded-full transition-all duration-500"
                style={{ width: `${Math.round(prog.ratio * 100)}%` }}
              />
            </div>
          </div>

          <div className="mt-2">
            <Button
              size="sm"
              variant="outline"
              disabled={regenerate.isPending}
              onClick={() => regenerate.mutate()}
            >
              {regenerate.isPending ? 'Relance…' : 'Relancer'}
            </Button>
            <p className="text-ink-400 mt-2 font-sans text-[11px]">
              Bloqué depuis plus de ~5 min ? Relance la génération.
            </p>
            {regenerate.isError && (
              <p className="text-accent-600 mt-1 font-sans text-[11px]">
                {regenerate.error?.message === 'already_running'
                  ? 'Encore en cours, patiente un peu.'
                  : regenerate.error?.message}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  const genFailed = genStatus === 'failed';

  const set = (patch: Partial<BlogArticle>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const setBlock = (i: number, patch: Partial<BlogBlock>) =>
    set({ blocks: draft.blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)) });
  const setLevel = (i: number, key: 'niveau1' | 'niveau2' | 'niveau3', content: string) => {
    const lvl: BlogLevel = { ...draft.blocks[i]![key], content };
    setBlock(i, { [key]: lvl } as Partial<BlogBlock>);
  };
  const metricsByBlock = new Map(metrics.map((m) => [m.block_index, m]));
  const LEVEL_ROWS = { niveau1: 2, niveau2: 4, niveau3: 6 } as const;

  // Liste de tous les cadres repliables (ordre d'affichage) — pour le « Tout (dé)plier ».
  const sectionIds = [
    'evaluation',
    'meta',
    'cover',
    ...draft.blocks.map((_, i) => `block-${i}`),
    'conclusion',
    'penseurs',
    'resources',
    'social',
    ...(admin ? ['social-publish'] : []),
    'seo',
  ];
  const toggleSection = (id: string) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const sectionProps = (id: string) => ({
    collapsible: true,
    collapsed: !openSections.has(id),
    onToggle: () => toggleSection(id),
  });
  const allOpen = openSections.size >= sectionIds.length;
  const toggleAllSections = () => setOpenSections(allOpen ? new Set() : new Set(sectionIds));

  const isPublished = draft.status === 'published' && draft.visibility === 'public';

  async function save() {
    await update.mutateAsync(editablePatch(draft!));
  }
  async function publish() {
    if (issues.length > 0) return;
    const res = await update.mutateAsync({
      ...editablePatch(draft!),
      status: 'published',
      visibility: 'public',
    });
    setDraft(normalizeBlogArticle(res.article));
  }
  async function unpublish() {
    const res = await update.mutateAsync({ status: 'draft', visibility: 'private' });
    setDraft((d) =>
      d ? { ...d, status: res.article.status, visibility: res.article.visibility } : d,
    );
  }
  // ── Modération (tuteur soumet ; admin approuve/refuse) ────────────────────
  // On enregistre d'abord les édits en cours, puis on mute l'état de publication,
  // puis on re-hydrate depuis l'article renvoyé (même règle que publish()).
  async function submitForReview() {
    if (issues.length > 0) return;
    await save();
    const res = await submitReview.mutateAsync();
    setDraft(normalizeBlogArticle(res.article));
  }
  async function withdrawForReview() {
    const res = await withdrawReview.mutateAsync();
    setDraft(normalizeBlogArticle(res.article));
  }
  async function moderateArticle(action: 'approve' | 'reject') {
    const res = await moderate.mutateAsync({ action });
    setDraft(normalizeBlogArticle(res.article));
  }
  async function generateCover() {
    const prompt = draft!.illustration_prompt || draft!.blocks[0]?.image_prompt || draft!.title;
    const res = await cover.mutateAsync({ prompt, title: draft!.title });
    if (res.url) set({ illustration_url: res.url });
  }
  // (Re)génère le sondage d'UN bloc depuis son contenu courant, puis le fusionne
  // dans le brouillon (les autres modifications non enregistrées sont préservées).
  async function generatePoll(i: number) {
    const block = draft!.blocks[i];
    if (!block) return;
    setPollError(null);
    setPollBusy(i);
    try {
      const res = await poll.mutateAsync({
        blockIndex: i,
        blockCount: draft!.blocks.length,
        title: draft!.title,
        block: {
          kind: block.kind,
          heading: block.heading,
          niveau1: block.niveau1.content,
          niveau2: block.niveau2.content,
          niveau3: block.niveau3.content,
        },
      });
      if (res.poll) setBlock(i, { poll: res.poll });
    } catch (err) {
      setPollError({ index: i, message: err instanceof Error ? err.message : String(err) });
    } finally {
      setPollBusy(null);
    }
  }

  // ─── Raffinage itératif : données dérivées + actions ───────────────────────
  // Pendant un run morcelé, on affiche le résultat PARTIEL (liveEval) ; sinon la
  // dernière évaluation persistée.
  const effectiveEval = liveEval ?? latestEval ?? null;
  const evalByBlock = new Map<number, BlogBlockEvaluation>();
  for (const be of effectiveEval?.blocks ?? []) evalByBlock.set(be.block_index, be);
  const versionsByBlock = new Map<number, BlogBlockVersion[]>();
  for (const v of versions) {
    const arr = versionsByBlock.get(v.block_index) ?? [];
    arr.push(v);
    versionsByBlock.set(v.block_index, arr);
  }
  const evalThreshold = effectiveEval?.threshold ?? threshold;
  // Les actions globales (parfait / régén-tout / plafond) s'appuient sur la
  // DERNIÈRE évaluation persistée et complète, jamais sur un partiel.
  const perfect =
    latestEval && !liveEval ? isArticlePerfect(latestEval, latestEval.threshold) : false;
  const belowIndices =
    latestEval && !liveEval ? blocksBelowThreshold(latestEval, latestEval.threshold) : [];
  const capReached = latestEval ? latestEval.iteration >= MAX_BLOG_EVAL_ITERATIONS : false;
  const refining = refineBusy !== null || globalRefining;

  // L'évaluation/raffinage portent sur la version PERSISTÉE : on tente d'abord
  // d'enregistrer le brouillon. Si la sauvegarde échoue (schéma strict sur un
  // champ non lié), on continue sur la dernière version enregistrée + avertit —
  // l'action ne doit jamais être bloquée par un souci de sauvegarde annexe.
  async function ensureSaved() {
    setSaveWarning(null);
    try {
      await save();
    } catch (err) {
      setSaveWarning(
        `Modifications non enregistrées — l'action porte sur la dernière version sauvegardée (${err instanceof Error ? err.message : String(err)}).`,
      );
    }
  }
  // Évaluation MORCELÉE : global → bloc par bloc → persist. Chaque appel est
  // court (pas d'idle-timeout) et le résultat s'affiche au fur et à mesure.
  async function runEvaluate() {
    if (!draft) return;
    setEvalError(null);
    setEvalRunning(true);
    const nBlocks = draft.blocks.length;
    try {
      await ensureSaved();
      setEvalProgress({ done: 0, total: nBlocks + 1, label: 'Analyse globale…' });
      const g = await evaluateBlogGlobal(articleId, threshold);
      let live: BlogEvaluation = {
        id: 'live',
        article_id: articleId,
        iteration: (latestEval?.iteration ?? 0) + 1,
        threshold,
        global_scores: g.global.scores,
        global_critique: g.global.critique,
        global_axes_progression: g.global.axes_progression,
        blocks: [],
        overall_score: null,
        is_perfect: false,
        model: null,
        created_at: '',
      };
      setLiveEval({ ...live });
      for (let i = 0; i < nBlocks; i++) {
        setEvalProgress({ done: i + 1, total: nBlocks + 1, label: `Bloc ${i + 1}/${nBlocks}…` });
        const r = await evaluateBlogBlock(articleId, threshold, i);
        live = { ...live, blocks: [...live.blocks, r.block] };
        setLiveEval({ ...live });
      }
      setEvalProgress({ done: nBlocks + 1, total: nBlocks + 1, label: 'Enregistrement…' });
      await persistBlogEvaluation(
        articleId,
        threshold,
        {
          scores: live.global_scores,
          critique: live.global_critique,
          axes_progression: live.global_axes_progression,
        },
        live.blocks,
      );
      await Promise.all([
        qc.invalidateQueries({ queryKey: blogEvaluationQueryKey(articleId) }),
        qc.invalidateQueries({ queryKey: blogEvaluationsQueryKey(articleId) }),
        qc.invalidateQueries({ queryKey: blockVersionsQueryKey(articleId) }),
      ]);
      setLiveEval(null); // la requête persistée prend le relais
    } catch (err) {
      setEvalError(err instanceof Error ? err.message : String(err));
      setLiveEval(null);
    } finally {
      setEvalRunning(false);
      setEvalProgress(null);
    }
  }
  async function refineOneBlock(i: number) {
    setRefineError(null);
    setRefineBusy(i);
    try {
      await ensureSaved();
      const res = await refine.mutateAsync({
        blockIndex: i,
        manualRemarks: manualRemarks[i],
        threshold,
      });
      setDraft(normalizeBlogArticle(res.article));
    } catch (err) {
      setRefineError(err instanceof Error ? err.message : String(err));
    } finally {
      setRefineBusy(null);
    }
  }
  async function refineAllBelow() {
    if (!latestEval) return;
    const indices = blocksBelowThreshold(latestEval, latestEval.threshold);
    if (indices.length === 0) return;
    setRefineError(null);
    setGlobalRefining(true);
    try {
      await ensureSaved(); // une seule sauvegarde : chaque refine persiste son bloc côté serveur
      let article: BlogArticle | null = null;
      for (const idx of indices) {
        setRefineBusy(idx);
        const res = await refine.mutateAsync({
          blockIndex: idx,
          manualRemarks: manualRemarks[idx],
          threshold,
        });
        article = res.article;
      }
      if (article) setDraft(normalizeBlogArticle(article));
    } catch (err) {
      setRefineError(err instanceof Error ? err.message : String(err));
    } finally {
      setRefineBusy(null);
      setGlobalRefining(false);
    }
  }
  async function switchVersion(versionId: string) {
    setRefineError(null);
    try {
      await ensureSaved();
      const article = await switchVer.mutateAsync(versionId);
      setDraft(normalizeBlogArticle(article));
    } catch (err) {
      setRefineError(err instanceof Error ? err.message : String(err));
    }
  }
  async function removeVersion(versionId: string) {
    setRefineError(null);
    try {
      await deleteVer.mutateAsync(versionId);
    } catch (err) {
      setRefineError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="mx-auto max-w-2xl p-4 pb-28 sm:p-6">
      <Link
        to="/tutor/blog"
        className="text-ink-500 hover:text-ink-900 mb-4 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Blog réflexif
      </Link>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className="font-display text-ink-900 text-[20px] font-extrabold">Éditer l'article</h1>
        <span className="bg-ink-100 text-ink-600 font-display rounded-full px-2 py-0.5 text-[11px] font-semibold">
          {blogStatusLabel(draft.visibility, draft.status)}
        </span>
        <Link
          to="/tutor/blog/$articleId/stats"
          params={{ articleId: article.id }}
          className="text-primary-600 inline-flex items-center gap-1 font-sans text-[12px] hover:underline"
        >
          <BarChart3 className="h-3.5 w-3.5" /> Statistiques
        </Link>
        {isPublished && SITE_URL && (
          <a
            href={`${SITE_URL}/blog/${draft.slug}`}
            target="_blank"
            rel="noreferrer"
            className="text-primary-600 inline-flex items-center gap-1 font-sans text-[12px] hover:underline"
          >
            Voir <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      {genFailed && (
        <div className="border-accent-200 bg-accent-50 mb-4 rounded-2xl border p-4">
          <p className="text-accent-700 font-display text-[14px] font-bold">
            La génération a échoué.
          </p>
          {article.generation_error && (
            <p className="text-accent-600 mt-1 break-words font-sans text-[12px]">
              {article.generation_error}
            </p>
          )}
          <Button
            size="sm"
            variant="outline"
            className="mt-3 flex items-center gap-1.5"
            disabled={regenerate.isPending}
            onClick={() => regenerate.mutate()}
          >
            <Sparkles className="h-4 w-4" />
            {regenerate.isPending ? 'Relance…' : 'Relancer la génération'}
          </Button>
        </div>
      )}

      <div className="mb-3 flex items-center justify-end">
        <button
          type="button"
          onClick={toggleAllSections}
          className="text-primary-600 hover:text-primary-700 inline-flex min-h-9 items-center gap-1 font-sans text-[12px] font-semibold active:scale-[0.98]"
        >
          {allOpen ? (
            <>
              <ChevronRight className="h-3.5 w-3.5" /> Tout replier
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5" /> Tout déplier
            </>
          )}
        </button>
      </div>

      <div className="space-y-4">
        {/* Évaluation & raffinage itératif */}
        <Section title="Évaluation & raffinage" {...sectionProps('evaluation')}>
          <div className="space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>Seuil (/10)</label>
                <input
                  type="number"
                  min={0}
                  max={10}
                  step={0.5}
                  className={`${inputCls} w-24`}
                  value={threshold}
                  onChange={(e) =>
                    setThreshold(Math.min(10, Math.max(0, Number(e.target.value) || 0)))
                  }
                />
              </div>
              <Button
                size="sm"
                className="flex items-center gap-1.5"
                disabled={update.isPending || evalRunning || refining}
                onClick={runEvaluate}
              >
                <Gauge className="h-4 w-4" />
                {evalRunning ? 'Évaluation…' : latestEval ? 'Réévaluer' : 'Évaluer'}
              </Button>
              {latestEval && !perfect && belowIndices.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1.5"
                  disabled={refining || update.isPending || evalRunning}
                  onClick={refineAllBelow}
                >
                  <RefreshCw className={`h-4 w-4 ${globalRefining ? 'animate-spin' : ''}`} />
                  Régénérer les {belowIndices.length} blocs sous le seuil
                </Button>
              )}
            </div>

            {/* Progression de l'évaluation morcelée */}
            {evalRunning && evalProgress && (
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <span className="font-display text-ink-700 text-[12px] font-semibold">
                    Évaluation… {evalProgress.label}
                  </span>
                  <span className="text-ink-400 font-sans text-[11px]">
                    {evalProgress.done}/{evalProgress.total}
                  </span>
                </div>
                <div className="bg-ink-100 h-1.5 w-full overflow-hidden rounded-full">
                  <div
                    className="bg-primary-500 h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.round((evalProgress.done / Math.max(1, evalProgress.total)) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {evalError && <p className="text-accent-600 font-sans text-[12px]">{evalError}</p>}
            {refineError && <p className="text-accent-600 font-sans text-[12px]">{refineError}</p>}
            {saveWarning && <p className="font-sans text-[12px] text-amber-600">{saveWarning}</p>}

            {!effectiveEval && !evalRunning && (
              <p className="text-ink-500 font-sans text-[13px]">
                Aucune évaluation. « Évaluer » note l&apos;article sur 5 axes (accessibilité,
                rythme, intérêt, pertinence, cible) au niveau global, par bloc et par sondage, puis
                repère ce qui est sous le seuil.
              </p>
            )}

            {effectiveEval && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-ink-700 text-[13px] font-bold">
                    {liveEval
                      ? 'Évaluation en cours'
                      : `Itération ${effectiveEval.iteration}/${MAX_BLOG_EVAL_ITERATIONS}`}
                  </span>
                  {effectiveEval.overall_score != null && (
                    <span className="bg-ink-100 text-ink-700 font-display rounded-full px-2 py-0.5 text-[11px] font-semibold">
                      global {effectiveEval.overall_score}/10
                    </span>
                  )}
                </div>
                <ScoreLine scores={effectiveEval.global_scores} threshold={evalThreshold} />
                {effectiveEval.global_critique && (
                  <p className="text-ink-600 whitespace-pre-wrap font-sans text-[12px]">
                    {effectiveEval.global_critique}
                  </p>
                )}
                <AxesList items={effectiveEval.global_axes_progression} />

                {perfect ? (
                  <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 p-3 font-sans text-[12px] text-green-700">
                    <CheckCircle2 className="h-4 w-4 shrink-0" /> Article parfait : toutes les notes
                    sont ≥ {evalThreshold}/10.
                  </div>
                ) : capReached ? (
                  <div className="border-accent-200 bg-accent-50 text-accent-700 rounded-xl border p-3 font-sans text-[12px]">
                    Plafond d&apos;itérations atteint ({MAX_BLOG_EVAL_ITERATIONS}). Affine à la main
                    ou ajuste le seuil.
                  </div>
                ) : belowIndices.length > 0 ? (
                  <p className="text-ink-500 font-sans text-[12px]">
                    Blocs sous le seuil : {belowIndices.map((i) => i + 1).join(', ')}.
                  </p>
                ) : null}

                {evalHistory.length > 1 && (
                  <p className="text-ink-400 font-mono text-[11px]">
                    progression :{' '}
                    {evalHistory
                      .map((e) => `it.${e.iteration} ${e.overall_score ?? '–'}`)
                      .join(' · ')}
                  </p>
                )}
              </div>
            )}
          </div>
        </Section>

        {/* Méta */}
        <Section title="Titre & métadonnées" {...sectionProps('meta')}>
          <div className="space-y-3">
            <div className="flex flex-col gap-1.5">
              <label className={labelCls}>Titre</label>
              <input
                className={inputCls}
                value={draft.title}
                onChange={(e) => set({ title: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={labelCls}>Sous-titre</label>
              <input
                className={inputCls}
                value={draft.subtitle}
                onChange={(e) => set({ subtitle: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={labelCls}>Slug</label>
              <input
                className={inputCls}
                value={draft.slug}
                onChange={(e) => set({ slug: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={labelCls}>
                Date de publication{' '}
                <span className="text-ink-400 font-normal">(modifiable, ex. antidater)</span>
              </label>
              <input
                type="date"
                className={inputCls}
                value={draft.published_at ? draft.published_at.slice(0, 10) : ''}
                onChange={(e) =>
                  set({
                    published_at: e.target.value
                      ? new Date(`${e.target.value}T12:00:00`).toISOString()
                      : null,
                  })
                }
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={labelCls}>
                Public visé{' '}
                <span className="text-ink-400 font-normal">(vide = tous les rôles)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {BLOG_TARGET_ROLE_OPTIONS.map((r) => (
                  <label key={r.value} className="flex cursor-pointer items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={draft.target_roles.includes(r.value)}
                      onChange={() =>
                        set({
                          target_roles: draft.target_roles.includes(r.value)
                            ? draft.target_roles.filter((v) => v !== r.value)
                            : [...draft.target_roles, r.value],
                        })
                      }
                      className="accent-primary-600"
                    />
                    <span className="font-sans text-[13px]">{r.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Couverture */}
        <Section title="Couverture" {...sectionProps('cover')}>
          {draft.illustration_url ? (
            <img
              src={draft.illustration_url}
              alt=""
              className="mb-3 aspect-video w-full rounded-xl object-cover"
            />
          ) : (
            <div className="bg-ink-50 text-ink-400 mb-3 flex aspect-video w-full items-center justify-center rounded-xl">
              <ImageIcon className="h-8 w-8" />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <label className={labelCls}>Prompt de couverture</label>
            <textarea
              className={areaCls}
              rows={2}
              value={draft.illustration_prompt ?? ''}
              onChange={(e) => set({ illustration_prompt: e.target.value })}
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-3 flex items-center gap-1.5"
            disabled={cover.isPending}
            onClick={generateCover}
          >
            <Sparkles className="h-4 w-4" />
            {cover.isPending ? 'Génération…' : 'Générer la couverture'}
          </Button>
          {cover.isError && (
            <p className="text-accent-600 mt-2 font-sans text-[12px]">{cover.error?.message}</p>
          )}
        </Section>

        {/* Blocs */}
        {draft.blocks.map((block, i) => {
          return (
            <Section
              key={i}
              title={`Bloc ${i + 1} — ${BLOG_BLOCK_KIND_LABELS[block.kind] ?? block.kind}`}
              subtitle={block.heading || undefined}
              {...sectionProps(`block-${i}`)}
            >
              <div className="space-y-3">
                {(() => {
                  const m = metricsByBlock.get(i);
                  return m ? (
                    <p className="text-ink-400 flex flex-wrap items-center gap-2 font-sans text-[11px]">
                      <span className="inline-flex items-center gap-0.5">
                        <Eye className="h-3 w-3" />
                        {m.level1_views} vues N1
                      </span>
                      <span>· {m.level2_opens} ouvertures N2</span>
                      <span>· {m.level3_opens} ouvertures N3</span>
                    </p>
                  ) : null;
                })()}

                {/* Évaluation « juge » du bloc + régénération + remarques manuelles */}
                {(() => {
                  const be = evalByBlock.get(i);
                  if (!be) return null;
                  const blockBelow = !scoresAboveThreshold(be.scores, evalThreshold);
                  const pollBelow =
                    be.poll != null && !scoresAboveThreshold(be.poll.scores, evalThreshold);
                  const flagged = blockBelow || pollBelow;
                  return (
                    <div
                      className={`rounded-xl border p-3 ${flagged ? 'border-accent-200 bg-accent-50/50' : 'border-ink-100 bg-ink-50/40'}`}
                    >
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <p className="font-display text-ink-600 text-[11px] font-bold uppercase tracking-wide">
                          Évaluation
                        </p>
                        <button
                          type="button"
                          disabled={refining || update.isPending || evalRunning}
                          onClick={() => refineOneBlock(i)}
                          className="text-primary-600 hover:text-primary-700 inline-flex min-h-9 items-center gap-1 font-sans text-[12px] font-semibold active:scale-[0.98] disabled:opacity-50"
                        >
                          <RefreshCw
                            className={`h-3.5 w-3.5 ${refineBusy === i ? 'animate-spin' : ''}`}
                          />
                          {refineBusy === i ? 'Régénération…' : 'Régénérer ce bloc'}
                        </button>
                      </div>
                      <ScoreLine scores={be.scores} threshold={evalThreshold} />
                      {be.critique && (
                        <p className="text-ink-600 mt-1 whitespace-pre-wrap font-sans text-[12px]">
                          {be.critique}
                        </p>
                      )}
                      <AxesList items={be.axes_progression} />
                      {be.poll && (
                        <p
                          className={`mt-1 font-mono text-[11px] ${pollBelow ? 'text-accent-600' : 'text-ink-400'}`}
                        >
                          sondage — {formatAxisScores(be.poll.scores)}
                        </p>
                      )}
                      <div className="mt-2 flex flex-col gap-1.5">
                        <label className={labelCls}>
                          Remarques manuelles{' '}
                          <span className="text-ink-400 font-normal">
                            (prises en compte à la régénération)
                          </span>
                        </label>
                        <textarea
                          className={areaCls}
                          rows={2}
                          placeholder="Ex. : ajoute un exemple chiffré, simplifie le niveau 1…"
                          value={manualRemarks[i] ?? ''}
                          onChange={(e) => setManualRemarks((m) => ({ ...m, [i]: e.target.value }))}
                        />
                      </div>
                    </div>
                  );
                })()}

                <div className="flex flex-col gap-1.5">
                  <label className={labelCls}>Titre du bloc</label>
                  <input
                    className={inputCls}
                    value={block.heading}
                    onChange={(e) => setBlock(i, { heading: e.target.value })}
                  />
                </div>
                {(['niveau1', 'niveau2', 'niveau3'] as const).map((key) => (
                  <div key={key} className="flex flex-col gap-1.5">
                    <label className={labelCls}>
                      {BLOG_LEVEL_LABELS[key]}{' '}
                      <span className="text-ink-400 font-normal">
                        {key === 'niveau1'
                          ? '(1-3 phrases)'
                          : key === 'niveau2'
                            ? '(1-2 paragraphes)'
                            : '(analyse, données, penseurs)'}
                      </span>
                    </label>
                    <textarea
                      className={areaCls}
                      rows={LEVEL_ROWS[key]}
                      value={block[key].content}
                      onChange={(e) => setLevel(i, key, e.target.value)}
                    />
                  </div>
                ))}
                <div className="flex flex-col gap-1.5">
                  <label className={labelCls}>Prompt d'illustration</label>
                  <input
                    className={inputCls}
                    value={block.image_prompt}
                    onChange={(e) => setBlock(i, { image_prompt: e.target.value })}
                  />
                </div>

                {/* Sondage — chaque bloc, synthèse comprise */}
                {
                  <div className="border-ink-100 bg-ink-50/40 rounded-xl border p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="font-display text-ink-600 text-[11px] font-bold uppercase tracking-wide">
                        Sondage
                      </p>
                      <button
                        type="button"
                        disabled={pollBusy !== null}
                        onClick={() => generatePoll(i)}
                        className="text-primary-600 hover:text-primary-700 inline-flex min-h-9 items-center gap-1 font-sans text-[12px] font-semibold active:scale-[0.98] disabled:opacity-50"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        {pollBusy === i ? 'Génération…' : 'Générer'}
                      </button>
                    </div>
                    {pollError?.index === i && (
                      <p className="text-accent-600 mb-2 font-sans text-[12px]">
                        {pollError.message}
                      </p>
                    )}
                    <input
                      className={`${inputCls} mb-2`}
                      placeholder="Question du sondage"
                      value={block.poll?.question ?? ''}
                      onChange={(e) =>
                        setBlock(i, {
                          poll: {
                            question: e.target.value,
                            choices: block.poll?.choices ?? ['', ''],
                          },
                        })
                      }
                    />
                    <div className="space-y-1.5">
                      {(block.poll?.choices ?? ['', '']).map((choice, ci) => (
                        <div key={ci} className="flex items-center gap-2">
                          <input
                            className={inputCls}
                            placeholder={`Choix ${ci + 1}`}
                            value={choice}
                            onChange={(e) => {
                              const choices = [...(block.poll?.choices ?? [])];
                              choices[ci] = e.target.value;
                              setBlock(i, {
                                poll: { question: block.poll?.question ?? '', choices },
                              });
                            }}
                          />
                          {(block.poll?.choices?.length ?? 0) > 2 && (
                            <button
                              type="button"
                              onClick={() => {
                                const choices = (block.poll?.choices ?? []).filter(
                                  (_, k) => k !== ci,
                                );
                                setBlock(i, {
                                  poll: { question: block.poll?.question ?? '', choices },
                                });
                              }}
                              className="text-ink-400 hover:text-accent-600 shrink-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {(block.poll?.choices?.length ?? 0) < 4 && (
                      <button
                        type="button"
                        onClick={() => {
                          const choices = [...(block.poll?.choices ?? []), ''];
                          setBlock(i, { poll: { question: block.poll?.question ?? '', choices } });
                        }}
                        className="text-primary-600 mt-2 inline-flex items-center gap-1 font-sans text-[12px] hover:underline"
                      >
                        <Plus className="h-3 w-3" /> Ajouter un choix
                      </button>
                    )}
                  </div>
                }

                {/* Versions conservées du bloc (switch / supprimer) */}
                {(() => {
                  const vs = versionsByBlock.get(i) ?? [];
                  if (vs.length <= 1) return null;
                  return (
                    <div className="border-ink-100 rounded-xl border p-3">
                      <p className="font-display text-ink-600 mb-2 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide">
                        <History className="h-3.5 w-3.5" /> Versions ({vs.length})
                      </p>
                      <div className="space-y-1.5">
                        {vs.map((v) => (
                          <div
                            key={v.id}
                            className={`flex flex-wrap items-center gap-2 rounded-lg border p-2 ${v.is_active ? 'border-primary-200 bg-primary-50/50' : 'border-ink-100'}`}
                          >
                            <span className="font-display text-ink-700 text-[12px] font-semibold">
                              {v.label || `v${v.version_no}`}
                            </span>
                            <span className="text-ink-400 font-sans text-[11px]">
                              {SOURCE_LABELS[v.source] ?? v.source}
                            </span>
                            {v.is_active && (
                              <span className="bg-primary-100 text-primary-700 font-display rounded-full px-1.5 py-0.5 text-[10px] font-bold">
                                active
                              </span>
                            )}
                            <span className="text-ink-400 basis-full font-mono text-[11px]">
                              {formatAxisScores(v.scores)}
                            </span>
                            <div className="ml-auto flex items-center gap-3">
                              {!v.is_active && (
                                <button
                                  type="button"
                                  disabled={refining || update.isPending || switchVer.isPending}
                                  onClick={() => switchVersion(v.id)}
                                  className="text-primary-600 hover:text-primary-700 inline-flex min-h-9 items-center font-sans text-[12px] font-semibold disabled:opacity-50"
                                >
                                  Utiliser
                                </button>
                              )}
                              {!v.is_active && (
                                <button
                                  type="button"
                                  disabled={deleteVer.isPending}
                                  onClick={() => removeVersion(v.id)}
                                  className="text-ink-400 hover:text-accent-600 disabled:opacity-50"
                                  aria-label="Supprimer la version"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </Section>
          );
        })}

        {/* Conclusion */}
        <Section title="Conclusion (question ouverte)" {...sectionProps('conclusion')}>
          <textarea
            className={areaCls}
            rows={3}
            value={draft.conclusion_md}
            onChange={(e) => set({ conclusion_md: e.target.value })}
          />
        </Section>

        {/* Penseurs */}
        <Section title="Les penseurs du sujet" {...sectionProps('penseurs')}>
          <ListEditor<BlogPenseur>
            items={draft.penseurs}
            onChange={(penseurs) => set({ penseurs })}
            empty={{ name: '', position: '', contribution: '', url: '' }}
            max={5}
            addLabel="Ajouter un penseur"
            render={(p, onField) => (
              <div className="space-y-2">
                <input
                  className={inputCls}
                  placeholder="Nom"
                  value={p.name}
                  onChange={(e) => onField({ name: e.target.value })}
                />
                <input
                  className={inputCls}
                  placeholder="Position"
                  value={p.position}
                  onChange={(e) => onField({ position: e.target.value })}
                />
                <textarea
                  className={areaCls}
                  rows={2}
                  placeholder="Contribution"
                  value={p.contribution}
                  onChange={(e) => onField({ contribution: e.target.value })}
                />
                <input
                  className={inputCls}
                  placeholder="URL (Wikipédia…) — vide = recherche auto"
                  value={p.url ?? ''}
                  onChange={(e) => onField({ url: e.target.value })}
                />
              </div>
            )}
          />
        </Section>

        {/* Pour aller plus loin */}
        <Section title="Pour aller plus loin" {...sectionProps('resources')}>
          {(['livres', 'etudes', 'articles', 'videos'] as const).map((group) => (
            <div key={group} className="mb-4 last:mb-0">
              <p className="font-display text-ink-600 mb-1.5 text-[12px] font-semibold capitalize">
                {group}
              </p>
              <ListEditor<BlogResource>
                items={draft.pour_aller_plus_loin[group]}
                onChange={(items) =>
                  set({ pour_aller_plus_loin: { ...draft.pour_aller_plus_loin, [group]: items } })
                }
                empty={{ title: '', author: '', url: '', why: '' }}
                max={8}
                addLabel="Ajouter"
                render={(r, onField) => (
                  <div className="space-y-2">
                    <input
                      className={inputCls}
                      placeholder="Titre"
                      value={r.title}
                      onChange={(e) => onField({ title: e.target.value })}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        className={inputCls}
                        placeholder="Auteur"
                        value={r.author ?? ''}
                        onChange={(e) => onField({ author: e.target.value })}
                      />
                      <input
                        className={inputCls}
                        placeholder="URL"
                        value={r.url ?? ''}
                        onChange={(e) => onField({ url: e.target.value })}
                      />
                    </div>
                    <input
                      className={inputCls}
                      placeholder="Pourquoi c'est intéressant"
                      value={r.why}
                      onChange={(e) => onField({ why: e.target.value })}
                    />
                  </div>
                )}
              />
            </div>
          ))}
        </Section>

        {/* Réseaux sociaux */}
        <Section title="Extraits réseaux sociaux" {...sectionProps('social')}>
          <div className="space-y-3">
            <div className="flex flex-col gap-1.5">
              <label className={labelCls}>LinkedIn</label>
              <textarea
                className={areaCls}
                rows={3}
                value={draft.social.linkedin}
                onChange={(e) => set({ social: { ...draft.social, linkedin: e.target.value } })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={labelCls}>Facebook</label>
              <textarea
                className={areaCls}
                rows={3}
                value={draft.social.facebook}
                onChange={(e) => set({ social: { ...draft.social, facebook: e.target.value } })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={labelCls}>Points forts du carrousel (2 max)</label>
              <p className="text-ink-400 font-sans text-[11px]">
                2 phrases courtes et percutantes mises en avant entre la couverture et le sondage du
                carrousel social.
              </p>
              <ListEditor<string>
                items={draft.social.instagram_carousel}
                onChange={(slides) =>
                  set({ social: { ...draft.social, instagram_carousel: slides } })
                }
                empty=""
                max={2}
                addLabel="Ajouter un point fort"
                render={(slide, _onField, onValue) => (
                  <input
                    className={inputCls}
                    value={slide}
                    onChange={(e) => onValue!(e.target.value)}
                  />
                )}
              />
            </div>
          </div>
        </Section>

        {/* Diffusion réseaux sociaux — RÉSERVÉE À L'ADMIN (comptes Jeraisonne). */}
        {admin && (
          <Section title="Publier sur les réseaux" {...sectionProps('social-publish')}>
            <SocialPublishSection
              articleId={article.id}
              isPublished={isPublished}
              initialCache={draft.social_carousel}
            />
          </Section>
        )}

        {/* SEO */}
        <Section title="SEO" {...sectionProps('seo')}>
          <div className="space-y-3">
            <div className="flex flex-col gap-1.5">
              <label className={labelCls}>Titre SEO ({draft.seo.title.length}/70)</label>
              <input
                className={inputCls}
                value={draft.seo.title}
                onChange={(e) => set({ seo: { ...draft.seo, title: e.target.value } })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={labelCls}>
                Méta-description ({draft.seo.meta_description.length}/170)
              </label>
              <textarea
                className={areaCls}
                rows={2}
                value={draft.seo.meta_description}
                onChange={(e) => set({ seo: { ...draft.seo, meta_description: e.target.value } })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={labelCls}>Mot-clé principal</label>
              <input
                className={inputCls}
                value={draft.seo.primary_keyword}
                onChange={(e) => set({ seo: { ...draft.seo, primary_keyword: e.target.value } })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={labelCls}>Mots-clés secondaires (séparés par des virgules)</label>
              <input
                className={inputCls}
                value={draft.seo.secondary_keywords.join(', ')}
                onChange={(e) =>
                  set({
                    seo: {
                      ...draft.seo,
                      secondary_keywords: e.target.value
                        .split(',')
                        .map((k) => k.trim())
                        .filter(Boolean),
                    },
                  })
                }
              />
            </div>
          </div>
        </Section>

        {issues.length > 0 && (
          <div className="border-accent-200 bg-accent-50 text-accent-700 rounded-xl border p-3 font-sans text-[12px]">
            <p className="mb-1 font-semibold">À corriger avant publication :</p>
            <ul className="list-inside list-disc space-y-0.5">
              {issues.map((iss, k) => (
                <li key={k}>{iss}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Barre d'actions fixe (mobile-first) — actions selon le rôle. */}
      <div className="border-ink-200 fixed inset-x-0 bottom-0 z-10 border-t bg-white/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center gap-2">
          <Button variant="outline" className="flex-1" disabled={update.isPending} onClick={save}>
            {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>

          {admin ? (
            // ── ADMIN : publication directe + modération des soumissions ──────
            <>
              {draft.visibility === 'pending_review' && (
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={moderate.isPending}
                  onClick={() => moderateArticle('reject')}
                >
                  Refuser
                </Button>
              )}
              {isPublished ? (
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={update.isPending}
                  onClick={unpublish}
                >
                  Dépublier
                </Button>
              ) : draft.visibility === 'pending_review' ? (
                <Button
                  className="flex-1"
                  disabled={moderate.isPending || issues.length > 0}
                  onClick={() => moderateArticle('approve')}
                >
                  Approuver & publier
                </Button>
              ) : (
                <Button
                  className="flex-1"
                  disabled={update.isPending || issues.length > 0}
                  onClick={publish}
                >
                  Publier
                </Button>
              )}
            </>
          ) : isPublished ? (
            // ── TUTEUR, article publié : lecture seule de l'état (édit. contenu) ─
            <span className="bg-xp-100 text-xp-700 font-display flex-1 rounded-xl px-3 py-2.5 text-center text-[13px] font-semibold">
              Publié
            </span>
          ) : draft.visibility === 'pending_review' ? (
            // ── TUTEUR, en attente : retrait possible ─────────────────────────
            <Button
              variant="outline"
              className="flex-1"
              disabled={withdrawReview.isPending}
              onClick={withdrawForReview}
            >
              {withdrawReview.isPending ? 'Retrait…' : 'Retirer'}
            </Button>
          ) : (
            // ── TUTEUR, brouillon : soumission à la validation admin ──────────
            <Button
              className="flex-1"
              disabled={submitReview.isPending || update.isPending || issues.length > 0}
              onClick={submitForReview}
            >
              {submitReview.isPending ? 'Envoi…' : 'Soumettre à la validation'}
            </Button>
          )}
        </div>

        {!admin && draft.visibility === 'pending_review' && (
          <p className="text-ink-500 mx-auto mt-2 max-w-2xl font-sans text-[12px]">
            En attente de validation par un administrateur.
          </p>
        )}
        {(update.isError || submitReview.isError || withdrawReview.isError || moderate.isError) && (
          <p className="text-accent-600 mx-auto mt-2 max-w-2xl font-sans text-[12px]">
            {update.error?.message ??
              submitReview.error?.message ??
              withdrawReview.error?.message ??
              moderate.error?.message}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Éditeur de liste générique (penseurs, ressources, slides) ───────────────

function ListEditor<T>({
  items,
  onChange,
  empty,
  max,
  addLabel,
  render,
}: {
  items: T[];
  onChange: (items: T[]) => void;
  empty: T;
  max: number;
  addLabel: string;
  render: (
    item: T,
    onField: (patch: Partial<T>) => void,
    onValue?: (v: T) => void,
  ) => React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="border-ink-100 relative rounded-xl border p-3 pr-9">
          {render(
            item,
            (patch) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it))),
            (v) => onChange(items.map((it, j) => (j === i ? v : it))),
          )}
          <button
            type="button"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            className="text-ink-400 hover:text-accent-600 absolute right-2 top-2"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      {items.length < max && (
        <button
          type="button"
          onClick={() => onChange([...items, empty])}
          className="text-primary-600 inline-flex items-center gap-1 font-sans text-[12px] hover:underline"
        >
          <Plus className="h-3 w-3" /> {addLabel}
        </button>
      )}
    </div>
  );
}
